import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconCheck, IconChevronRight, IconSearch, IconX } from '@tabler/icons-react';
import { count, matchesSearch, searchText, sourceLabel } from './slabModel.mjs';
import './SlabFilters.css';

export const FILTER_TITLES = {
  sets: 'Set', rarities: 'Rarity', editions: 'Edition', variants: 'Variant / finish', years: 'Year',
  grades: 'Grade', graders: 'Grader', languages: 'Language', categories: 'Category', labels: 'Labels', hiddenCategories: 'Hide categories', hiddenLabels: 'Hide labels', source: 'Held in', priceStatus: 'Pricing status',
};

export function selectedFilterValues(filters, key) {
  return key === 'source' ? (filters.source && filters.source !== 'all' ? [filters.source] : []) : filters[key] || [];
}

function FilterPicker({ title, options, selected, multiple, onToggle, hiding }) {
  const [query, setQuery] = useState('');
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = options.filter(option => matchesSearch(searchText(option.label || option.key), tokens));
  return (
    <div className="sv-filter-picker">
      <div className="sv-filter-picker-search">
        <label className="sv-filter-search">
          <IconSearch size={18} stroke={1.7} aria-hidden="true" />
          <input type="search" aria-label={`Search ${title.toLowerCase()}`} placeholder={`Search ${count(options.length)} options…`}
            value={query} onChange={event => setQuery(event.target.value)} autoComplete="off" autoCorrect="off" spellCheck="false" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear option search"><IconX size={16} aria-hidden="true" /></button>}
        </label>
        <p aria-live="polite">{selected.length ? `${count(selected.length)} ${hiding ? 'hidden' : 'selected'}` : hiding ? 'Nothing hidden' : 'All included'}<span>{multiple ? 'Choose one or more' : 'Choose one'}</span></p>
      </div>
      <div className="sv-filter-picker-options" role="group" aria-label={`${title} options`}>
        {shown.map(option => {
          const checked = selected.includes(option.key);
          return (
            <button key={option.key} type="button" className="sv-filter-option" role="checkbox" aria-checked={checked} onClick={() => onToggle(option.key)}>
              <span className="sv-filter-check" aria-hidden="true">{checked && <IconCheck size={15} stroke={2.5} />}</span>
              <span className="sv-filter-option-label">{option.label || option.key}</span>
              {option.count != null && <span className="sv-filter-option-count">{count(option.count)}</span>}
            </button>
          );
        })}
        {!shown.length && <p className="sv-filter-empty">No options match “{query}”.</p>}
      </div>
    </div>
  );
}

export default function SlabFilters({ facets, filters, onChange, picker, onPickerChange }) {
  const root = useRef(null);
  const triggers = useRef({});
  const overviewScroll = useRef(0);
  const lastPicker = useRef(null);
  const options = useMemo(() => ({ ...facets, hiddenCategories: facets.categories, hiddenLabels: facets.labels, source: facets.sources.map(option => ({ ...option, label: sourceLabel(option.key) })) }), [facets]);

  useLayoutEffect(() => {
    const scroll = root.current?.closest('.sv-sheet-body');
    if (!scroll) return;
    if (picker) {
      scroll.scrollTop = 0;
      lastPicker.current = picker;
      scroll.closest('.sv-sheet')?.querySelector('.sv-sheet-back')?.focus({ preventScroll: true });
    } else {
      scroll.scrollTop = overviewScroll.current;
      triggers.current[lastPicker.current]?.focus({ preventScroll: true });
    }
  }, [picker]);

  function open(key) {
    overviewScroll.current = root.current?.closest('.sv-sheet-body')?.scrollTop || 0;
    onPickerChange(key);
  }

  function toggle(key, value) {
    if (key === 'source') {
      onChange({ ...filters, source: filters.source === value ? 'all' : value });
      return;
    }
    const selected = selectedFilterValues(filters, key);
    onChange({ ...filters, [key]: selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value] });
  }

  function fields(keys) {
    return <div className="sv-filter-fields">{keys.filter(key => (options[key]?.length > (key.startsWith('hidden') ? 0 : 1) || selectedFilterValues(filters, key).length)).map(key => {
      const selected = selectedFilterValues(filters, key);
      const summary = selected.length === 1 ? options[key]?.find(option => option.key === selected[0])?.label || selected[0] : selected.length ? `${count(selected.length)} ${key.startsWith('hidden') ? 'hidden' : 'selected'}` : key.startsWith('hidden') ? 'None' : 'All';
      return <button key={key} ref={element => { triggers.current[key] = element; }} type="button" className="sv-filter-field" aria-label={`${FILTER_TITLES[key]}: ${summary}`} aria-haspopup="dialog" onClick={() => open(key)}>
        <span>{FILTER_TITLES[key]}</span><span className={`sv-filter-field-value${selected.length ? ' is-selected' : ''}`} title={summary}>{summary}</span><IconChevronRight size={17} stroke={1.7} aria-hidden="true" />
      </button>;
    })}</div>;
  }

  return <div className="sv-filter-content" ref={root}>
    {picker ? <FilterPicker key={picker} title={FILTER_TITLES[picker]} options={[...(options[picker] || []), ...selectedFilterValues(filters, picker).filter(value => !(options[picker] || []).some(option => option.key === value)).map(key => ({ key, label: picker === 'source' ? sourceLabel(key) : key, count: 0 }))]} selected={selectedFilterValues(filters, picker)} multiple={picker !== 'source'} hiding={picker.startsWith('hidden')} onToggle={value => toggle(picker, value)} /> : (
      <div className="sv-filters">
        <fieldset className="sv-filter-basis">
          <legend>Price reference</legend>
          <div role="group" aria-label="Price reference">{['PSA', 'ACE'].map(basis => <button key={basis} type="button" aria-pressed={(filters.priceBasis || 'PSA') === basis} onClick={() => onChange({ ...filters, priceBasis: basis })}>{basis}</button>)}</div>
        </fieldset>
        <section className="sv-filter-section" aria-label="Pricing"><h3>Pricing</h3>{fields(['priceStatus'])}<small className="sv-price-range-note">Total uses available {(filters.priceBasis || 'PSA')} sales. Ranges use their lower end; missing prices are not zero.</small></section>
        <section className="sv-filter-section" aria-label="Card details"><h3>Card details</h3>{fields(['sets', 'rarities', 'editions', 'variants', 'years'])}</section>
        <section className="sv-filter-section" aria-label="Grading"><h3>Grading</h3>{fields(['graders', 'grades', 'languages'])}</section>
        <section className="sv-filter-section" aria-label="Inventory"><h3>Inventory</h3>
          <small className="sv-price-range-note">All current categories are included unless you choose a filter. Picked or removed slabs stay out until scanned back into a box. Their records remain in Movement history.</small>
          {fields(['categories', 'labels', 'source'])}
        </section>
        <section className="sv-filter-section" aria-label="Hide from this view"><h3>Hide from this view</h3>
          <small className="sv-price-range-note">Hide these slabs while you browse or make a pick list. This is a view filter, not a permanent lock on listings or movements; existing picks are unaffected.</small>
          {fields(['hiddenCategories', 'hiddenLabels'])}
          <label className="sv-filter-notes">Hide notes containing<input value={filters.hiddenNoteQuery || ''} onChange={event => onChange({ ...filters, hiddenNoteQuery: event.target.value })} placeholder="e.g. Neurotic Trade" /></label>
          <small className="sv-price-range-note">Only notes visible to your account can be matched.</small>
        </section>
        <label className="sv-filter-notes">Search notes<input value={filters.noteQuery || ''} onChange={event => onChange({ ...filters, noteQuery: event.target.value })} placeholder="Trade, Yorkshire…" /></label>
        <fieldset className="sv-filter-price">
          <legend>Value</legend>
          <div className="sv-range">
            <label><span>From</span><div><i aria-hidden="true">£</i><input inputMode="decimal" value={filters.min} placeholder="0" onChange={event => onChange({ ...filters, min: event.target.value })} /></div></label>
            <label><span>To</span><div><i aria-hidden="true">£</i><input inputMode="decimal" value={filters.max} placeholder="Any" onChange={event => onChange({ ...filters, max: event.target.value })} /></div></label>
          </div>
          <small className="sv-price-range-note">Sale ranges use their lower end.</small>
        </fieldset>
      </div>
    )}
  </div>;
}
